import { Injectable, EventEmitter } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { lastValueFrom } from 'rxjs';

import { UtilityService } from '@core/services/util/utility.service';
import { LocalStorageService } from '@core/services/util/local-storage.service';
import { StorageService } from '@core/services/util/storage.service';
import { SessionStorageService } from '@core/services/util/session-storage.service';

import { environment } from '@environment';
import { GtmService } from '@core/services/analytics/gtm.service';
import { FirebaseService } from '../pushNotification/firebase.service';

@Injectable({
    providedIn: 'root'
})
export class LoginService {
    baseUrl: string;
    accountsApiUrl: string;

    userLoginSuccess: EventEmitter<any> = new EventEmitter();
    userLogoutSuccess: EventEmitter<boolean> = new EventEmitter();

    cookie_keys = [
        'customer_id',
        'name',
        'email',
        'phone_no',
        'session-id',
        'referral_code',
        'birthday',
        'sbda',
        'custId',
        'new_customer',
        'last_order_number_of_days',
        'address_id',
        'address_type'
    ];

    constructor(
        private http: HttpClient,
        private storageService: StorageService,
        private utilityService: UtilityService,
        private localStorage: LocalStorageService,
        private gtmService: GtmService,
        private sessionStorage: SessionStorageService,
        private firebaseService: FirebaseService
    ) {
        this.baseUrl = environment.apiUrl;
        this.accountsApiUrl = environment.accountsApiUrl;
    }

    setCustomerDetailsFromChat(data) {
        const expiryDate = this.utilityService.getDefaultCookieExpiryTime();
        this.storageService.set('name', data.name, expiryDate);
        this.storageService.set('phone_no', data.phone_no, expiryDate);
        this.storageService.set('email', data.email, expiryDate);
        this.storageService.set('session-id', data.session_id, expiryDate);
        this.storageService.delete('unsigned_session_id');
    }

    setCustomerDetails(customer, performAfterLoginTask = true) {
        const expiryDate = this.utilityService.getDefaultCookieExpiryTime();
        this.storageService.set('name', customer.name, expiryDate);
        this.storageService.set('phone_no', customer.phone_no, expiryDate);
        this.storageService.set('email', customer.email, expiryDate);
        this.storageService.set('referral_code', customer.referral_code || '', expiryDate);

        this.localStorage.set('addresses', customer.customer_addresses);
        this.storageService.set('customer_id', customer.customer_id, expiryDate);
        this.storageService.set('custId', customer.id, expiryDate);
        this.storageService.delete('phone_number');
        this.storageService.delete('unsigned_session_id');

        if (customer.customer_info) {
            this.storageService.set('birthday', customer.customer_info.birthday, expiryDate);
        }

        if (performAfterLoginTask) {
            this.userLoginSuccess.emit(customer);
            this.gtmService.logLoginPath();
        }
    }

    async getUnsignedSessionId() {
        const device_id = this.utilityService.generateRandomDeviceId();
        const device_token = this.localStorage.get('device_token');

        return new Promise(resolve => {
            this.http
                .get(this.accountsApiUrl + '/device/register', {
                    params: {
                        brand_id: environment.brand.id,
                        device_id: device_id,
                        platform: environment.platform,
                        device_token: device_token ? device_token : null,
                        token: ''
                    }
                })
                .subscribe(data => {
                    return resolve(data);
                });
        });
    }

    ensureSessionId(signed = false) {
        let sessionId = this.storageService.get('unsigned_session_id');

        if (signed) {
            sessionId = this.storageService.get('session-id') || sessionId;
        }

        if (!sessionId) {
            return this.getUnsignedSessionId().then((data: any) => {
                this.storageService.set('unsigned_session_id', data.unsigned_auth_token);
                return data.unsigned_auth_token;
            });
        } else {
            return Promise.resolve(sessionId);
        }
    }

    clearSessions() {
        this.storageService.delete('phone_no');
        this.storageService.delete('session-id');
        this.storageService.delete('unsigned_session_id');
    }

    clearAllData() {
        const _this = this;
        this.cookie_keys.forEach(function (cookie_key) {
            _this.storageService.delete(cookie_key);
        });

        this.sessionStorage.remove('faq_details');
        this.sessionStorage.remove('feedback-modal-tracking-id');
        this.sessionStorage.remove('feedback-strip-tracking-id');

        this.localStorage.remove('addresses');
        this.localStorage.remove('addresses_v2');
        this.userLogoutSuccess.emit(true);
    }

    logOutApi() {
        const headers = this.utilityService.getAuthHeaders('signed');
        return this.http.post(this.accountsApiUrl + '/customers/sign_out', {}, { headers });
    }

    deleteAccount() {
        const mobileNumber = this.storageService.get('phone_no');
        const headers = this.utilityService.getAuthHeaders('signed');
        return this.http.put(
            this.accountsApiUrl + '/customers/deactivate',
            { phone_no: mobileNumber, brand_id: environment.brand.id },
            { headers }
        );
    }

    async phoneStatus(mobileNumber, sendAppCheckToken?: boolean): Promise<any> {

        let app_check_token = { token: '' };

        if (sendAppCheckToken !== false) {
            app_check_token = await this.firebaseService.getAppCheckToken('check_phone_status');
        }

        const headers = this.utilityService.getAuthHeaders('unsigned');

        return lastValueFrom(
            this.http.post(
                this.accountsApiUrl + '/customers/check_phone_status',
                { phone_no: mobileNumber },
                {
                    headers: { ...headers, 'X-Firebase-AppCheck': app_check_token.token }
                }
            )
        );
    }

    async signinWithPassword(password) {
        const mobileNumber = this.storageService.get('phone_number');
        const headers = this.utilityService.getAuthHeaders('unsigned');

        return new Promise((resolve, reject) => {
            this.http
                .post(
                    this.accountsApiUrl + '/customers/sign_in',
                    {
                        phone_no: mobileNumber,
                        password
                    },
                    { headers }
                )
                .subscribe({
                    next: (data: any) => {
                        return resolve(data);
                    },
                    error: (error: any) => {
                        return reject(error);
                    }
                });
        });
    }

    generateOtp(login_method: string) {
        const mobileNumber = this.storageService.get('phone_number');
        const headers = this.utilityService.getAuthHeaders('unsigned');

        return this.http.post(
            this.accountsApiUrl + '/customers/sign_in',
            {
                initiate: 'true',
                phone_no: mobileNumber,
                login_method
            },
            { headers }
        );
    }

    signinWithOtp(otp) {
        const mobileNumber = this.storageService.get('phone_number');
        const headers = this.utilityService.getAuthHeaders('unsigned');

        return this.http.post(
            this.accountsApiUrl + '/customers/sign_in',
            {
                phone_no: mobileNumber,
                login_method: 'otp',
                otp
            },
            { headers }
        );
    }

    signinWithGoogle(id_token) {
        const headers = this.utilityService.getAuthHeaders('unsigned');
        return this.http.post(
            this.accountsApiUrl + '/customers/sign_in',
            {
                id_token,
                login_method: 'google'
            },
            { headers }
        );
    }

    signUp(name, email, password) {
        const mobileNumber = this.storageService.get('phone_number');
        const headers = this.utilityService.getAuthHeaders('unsigned');

        return this.http.post(
            this.accountsApiUrl + '/customers/sign_up',
            {
                phone_no: mobileNumber,
                name,
                email,
                password
            },
            { headers }
        );
    }

    initiateResetPassword() {
        const mobileNumber = this.storageService.get('phone_number');
        const headers = this.utilityService.getAuthHeaders('unsigned');

        return this.http.post(
            this.accountsApiUrl + '/customers/reset_password',
            {
                phone_no: mobileNumber
            },
            { headers }
        );
    }

    resetPassword(password, confirmPassword, token) {
        const mobileNumber = this.storageService.get('phone_number');
        const headers = this.utilityService.getAuthHeaders('unsigned');

        return this.http.put(
            this.accountsApiUrl + '/customers/reset_password',
            {
                phone_no: mobileNumber,
                password,
                password_confirmation: confirmPassword,
                token
            },
            { headers }
        );
    }

    changeNumber(mobileNumber) {
        return this.http.post(
            this.accountsApiUrl + '/customers/change_phone',
            {
                phone_no: mobileNumber
            },
            { headers: this.utilityService.getAuthHeaders('') }
        );
    }

    updateToken() {
        return this.http.get(this.accountsApiUrl + '/customers/get_refresh_token', {
            headers: this.utilityService.getAuthHeaders()
        });
    }
}

